import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { httpDELETE, httpGET, httpPOST, httpPUT } from '../../services/http';
import {
  CreateVariantRequest,
  Manufacturer,
  Product,
  ProductVariant,
  Series,
  UpdateVariantRequest,
} from '../../types/catalogue';
import { toastReducer } from '../reducers/toast.reducer';
import { catalogueReducer } from './catalogue.reducer';
import {
  getCatalogueSelectedVariantId,
  isManufacturerSeriesFetched,
  isProductDetailFetched,
  isSeriesProductsFetched,
} from './catalogue.selectors';

const {
  // Saga triggers
  fetchManufacturers,
  fetchSeriesByManufacturer,
  fetchProductsBySeries,
  fetchProductDetail,
  createManufacturer,
  modifyManufacturer,
  removeManufacturer,
  createSeries,
  modifySeries,
  removeSeries,
  createProduct,
  modifyProduct,
  removeProduct,
  createVariant,
  modifyVariant,
  removeVariant,
  syncCatalogue,
  // State mutations
  setManufacturers,
  setLoading,
  setLoadingProductDetail,
  setError,
  addManufacturerToList,
  updateManufacturerInList,
  removeManufacturerFromList,
  setSeriesForManufacturer,
  addSeriesToManufacturer,
  updateSeriesInList,
  removeSeriesFromList,
  setProductsForSeries,
  addProductToSeries,
  updateProductInList,
  removeProductFromList,
  setProductDetail,
  addVariantToProduct,
  updateVariantInList,
  removeVariantFromList,
  selectVariant,
  // Selection (watched for auto-fetch)
  selectManufacturer,
  selectSeries,
  selectProduct,
} = catalogueReducer.actions;

const { showToast } = toastReducer.actions;

// ─── Helper: build multipart FormData for variant endpoints ─────────────────

function buildVariantFormData(variantData: CreateVariantRequest | UpdateVariantRequest, ldcFile?: File): FormData {
  const formData = new FormData();
  formData.append('variant', new Blob([JSON.stringify(variantData)], { type: 'application/json' }));
  if (ldcFile) {
    formData.append('ldcFile', ldcFile);
  }
  return formData;
}

// ─── Fetch handlers ──────────────────────────────────────────────────────────

function* fetchManufacturersHandler() {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const { data: apiData } = (yield call(httpGET, '/catalogue/manufacturers')) as AxiosResponse;

    const transformedManufacturers: Manufacturer[] = (Array.isArray(apiData) ? apiData : []).map(
      (m: { id: string; name: string; description?: string; logoBase64?: string; logoUrl?: string }) => ({
        id: String(m.id),
        name: m.name,
        description: m.description,
        logo: m.logoUrl || m.logoBase64,
        series: [],
      }),
    );

    yield put(setManufacturers(transformedManufacturers));
  } catch (err: unknown) {
    console.error('Failed to fetch manufacturers:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to load manufacturers';
    yield put(setError(errorMessage));
    yield put(setManufacturers([]));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchSeriesByManufacturerHandler(action: PayloadAction<string>) {
  const manufacturerId = action.payload;
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const { data: apiData } = (yield call(httpGET, `/catalogue/manufacturers/${manufacturerId}`)) as AxiosResponse;
    const seriesData = apiData?.series || apiData?.Series || [];

    const transformedSeries: Series[] = (Array.isArray(seriesData) ? seriesData : []).map(
      (s: { id: string; name: string; description?: string }) => ({
        id: String(s.id),
        name: s.name,
        description: s.description,
        products: [],
      }),
    );

    yield put(
      setSeriesForManufacturer({
        manufacturerId,
        series: transformedSeries,
        manufacturerUpdates: {
          name: apiData?.name,
          description: apiData?.description,
          logo: apiData?.logoUrl || apiData?.logoBase64,
        },
      }),
    );
  } catch (err: unknown) {
    console.error('Failed to fetch series:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to load series';
    yield put(setError(errorMessage));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchProductsBySeriesHandler(action: PayloadAction<string>) {
  const seriesId = action.payload;
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const { data: apiData } = (yield call(httpGET, `/catalogue/series/${seriesId}`)) as AxiosResponse;
    const productsData = apiData?.products || apiData?.Products || [];

    const transformedProducts: Product[] = (Array.isArray(productsData) ? productsData : []).map(
      (p: {
        id: string;
        name: string;
        description?: string;
        status?: string;
        imageBase64?: string;
        imageUrl?: string;
      }) => ({
        id: String(p.id),
        name: p.name,
        description: p.description,
        status: p.status,
        imageUrl: p.imageUrl || p.imageBase64,
        thumbnails: [],
        variants: [],
      }),
    );

    yield put(
      setProductsForSeries({
        seriesId,
        products: transformedProducts,
        seriesUpdates: {
          name: apiData?.name,
          description: apiData?.description,
        },
      }),
    );
  } catch (err: unknown) {
    console.error('Failed to fetch products:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to load products';
    yield put(setError(errorMessage));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchProductDetailHandler(action: PayloadAction<string>) {
  const productId = action.payload;
  yield put(setLoadingProductDetail(true));
  yield put(setError(null));
  try {
    const { data: apiData } = (yield call(httpGET, `/catalogue/products/${productId}`)) as AxiosResponse;
    const variantsData = apiData?.variants || apiData?.Variants || [];

    const transformedVariants: ProductVariant[] = (Array.isArray(variantsData) ? variantsData : []).map(
      (v: {
        id: string;
        name: string;
        power?: number;
        lumenOutput?: number;
        colorTemperature?: number;
        cri?: number;
        lifespan?: number;
        ipRating?: string;
        absolutePhotometry?: boolean;
        manufacturerUrl?: string;
        ldcFileUrl?: string;
      }) => ({
        id: String(v.id),
        name: v.name,
        power: v.power,
        lumenOutput: v.lumenOutput,
        colorTemperature: v.colorTemperature,
        manufacturerUrl: v.manufacturerUrl,
        ldcFileUrl: v.ldcFileUrl,
        specifications: {
          absolutePhotometry: v.absolutePhotometry != null ? String(v.absolutePhotometry) : undefined,
          luminousFlux: v.lumenOutput != null ? `${Math.round(v.lumenOutput).toLocaleString()} lm` : undefined,
          power: v.power != null ? `${v.power}W` : undefined,
          colorTemperature: v.colorTemperature != null ? `${v.colorTemperature} K` : undefined,
          cri: v.cri != null ? String(v.cri) : undefined,
          lifespan: v.lifespan != null ? `${v.lifespan} h` : undefined,
          ipRating: v.ipRating || undefined,
        },
      }),
    );

    yield put(
      setProductDetail({
        productId,
        changes: {
          name: apiData?.name,
          description: apiData?.description,
          status: apiData?.status,
          imageUrl: apiData?.imageUrl || apiData?.imageBase64,
          variants: transformedVariants,
        },
      }),
    );

    // Auto-select first variant if none is currently selected
    const currentVariantId = (yield select(getCatalogueSelectedVariantId)) as string | null;
    if (!currentVariantId && transformedVariants.length > 0) {
      yield put(selectVariant(transformedVariants[0].id));
    }
  } catch (err: unknown) {
    console.error('Failed to fetch product detail:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to load product details';
    yield put(setError(errorMessage));
  } finally {
    yield put(setLoadingProductDetail(false));
  }
}

// ─── Manufacturer CRUD ───────────────────────────────────────────────────────

function* createManufacturerHandler(action: PayloadAction<{ name: string; logoBase64?: string }>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const { data } = (yield call(httpPOST, '/catalogue/manufacturers', action.payload)) as AxiosResponse;
    const newManufacturer: Manufacturer = {
      id: String(data.id),
      name: data.name || action.payload.name,
      description: data.description,
      logo: data.logoUrl || data.logoBase64,
      series: [],
    };
    yield put(addManufacturerToList(newManufacturer));
  } catch (err: unknown) {
    console.error('Failed to create manufacturer:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to create manufacturer';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* modifyManufacturerHandler(
  action: PayloadAction<{ id: string; data: { name?: string; description?: string; logoBase64?: string } }>,
) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    yield call(httpPUT, `/catalogue/manufacturers/${action.payload.id}`, action.payload.data);
    yield put(
      updateManufacturerInList({
        id: action.payload.id,
        changes: {
          name: action.payload.data.name,
          description: action.payload.data.description,
          logo: action.payload.data.logoBase64,
        },
      }),
    );
  } catch (err: unknown) {
    console.error('Failed to update manufacturer:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to update manufacturer';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* removeManufacturerHandler(action: PayloadAction<string>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    yield call(httpDELETE, `/catalogue/manufacturers/${action.payload}`, {});
    yield put(removeManufacturerFromList(action.payload));
  } catch (err: unknown) {
    console.error('Failed to delete manufacturer:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to delete manufacturer';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

// ─── Series CRUD ─────────────────────────────────────────────────────────────

function* createSeriesHandler(action: PayloadAction<{ manufacturerId: string; name: string; description?: string }>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const { data } = (yield call(httpPOST, `/catalogue/manufacturers/${action.payload.manufacturerId}/series`, {
      name: action.payload.name,
      description: action.payload.description,
    })) as AxiosResponse;
    const newSeries: Series = {
      id: data.id,
      name: data.name || action.payload.name,
      description: data.description,
      products: [],
    };
    yield put(addSeriesToManufacturer({ manufacturerId: action.payload.manufacturerId, series: newSeries }));
  } catch (err: unknown) {
    console.error('Failed to create series:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to create series';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* modifySeriesHandler(action: PayloadAction<{ id: string; data: { name?: string; description?: string } }>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    yield call(httpPUT, `/catalogue/series/${action.payload.id}`, action.payload.data);
    yield put(
      updateSeriesInList({
        id: action.payload.id,
        changes: action.payload.data,
      }),
    );
  } catch (err: unknown) {
    console.error('Failed to update series:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to update series';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* removeSeriesHandler(action: PayloadAction<string>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    yield call(httpDELETE, `/catalogue/series/${action.payload}`, {});
    yield put(removeSeriesFromList(action.payload));
  } catch (err: unknown) {
    console.error('Failed to delete series:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to delete series';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

// ─── Product CRUD ────────────────────────────────────────────────────────────

function* createProductHandler(
  action: PayloadAction<{ seriesId: string; name: string; description?: string; imageBase64?: string }>,
) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const { data } = (yield call(httpPOST, `/catalogue/series/${action.payload.seriesId}/products`, {
      name: action.payload.name,
      description: action.payload.description,
      imageBase64: action.payload.imageBase64,
    })) as AxiosResponse;
    const newProduct: Product = {
      id: data.id,
      name: data.name || action.payload.name,
      description: data.description,
      imageUrl: data.imageUrl || data.imageBase64,
      thumbnails: [],
      variants: [],
    };
    yield put(addProductToSeries({ seriesId: action.payload.seriesId, product: newProduct }));
  } catch (err: unknown) {
    console.error('Failed to create product:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to create product';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* modifyProductHandler(
  action: PayloadAction<{
    id: string;
    data: { name?: string; description?: string; status?: string; imageBase64?: string };
  }>,
) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    yield call(httpPUT, `/catalogue/products/${action.payload.id}`, action.payload.data);
    yield put(
      updateProductInList({
        id: action.payload.id,
        changes: {
          name: action.payload.data.name,
          description: action.payload.data.description,
          status: action.payload.data.status,
          imageUrl: action.payload.data.imageBase64,
        },
      }),
    );
  } catch (err: unknown) {
    console.error('Failed to update product:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to update product';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* removeProductHandler(action: PayloadAction<string>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    yield call(httpDELETE, `/catalogue/products/${action.payload}`, {});
    yield put(removeProductFromList(action.payload));
  } catch (err: unknown) {
    console.error('Failed to delete product:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to delete product';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

// ─── Variant CRUD ────────────────────────────────────────────────────────────

function* createVariantHandler(
  action: PayloadAction<{ productId: string; data: CreateVariantRequest; ldcFile?: File }>,
) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const formData = buildVariantFormData(action.payload.data, action.payload.ldcFile);
    const { data } = (yield call(
      httpPOST,
      `/catalogue/products/${action.payload.productId}/variants`,
      formData,
    )) as AxiosResponse;
    const cct = data.colorTemperature || action.payload.data.colorTemperature;
    const lumenOutput = data.lumenOutput || action.payload.data.lumenOutput;
    const power = data.power || action.payload.data.power;
    const newVariant: ProductVariant = {
      id: data.id,
      name: data.name || action.payload.data.name,
      power,
      lumenOutput,
      colorTemperature: cct,
      manufacturerUrl: data.manufacturerUrl || action.payload.data.manufacturerUrl,
      ldcFileUrl: data.ldcFileUrl,
      specifications: {
        absolutePhotometry: data.absolutePhotometry != null ? String(data.absolutePhotometry) : undefined,
        luminousFlux: lumenOutput != null ? `${Math.round(lumenOutput).toLocaleString()} lm` : undefined,
        power: power != null ? `${power}W` : undefined,
        colorTemperature: cct != null ? `${cct} K` : undefined,
        cri: data.cri != null ? String(data.cri) : undefined,
        lifespan: data.lifespan != null ? `${data.lifespan} h` : undefined,
        ipRating: data.ipRating || undefined,
      },
    };
    yield put(addVariantToProduct({ productId: action.payload.productId, variant: newVariant }));
  } catch (err: unknown) {
    console.error('Failed to create variant:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to create variant';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* modifyVariantHandler(action: PayloadAction<{ id: string; data: UpdateVariantRequest; ldcFile?: File }>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    const formData = buildVariantFormData(action.payload.data, action.payload.ldcFile);
    const { data } = (yield call(httpPUT, `/catalogue/variants/${action.payload.id}`, formData)) as AxiosResponse;
    yield put(
      updateVariantInList({
        id: action.payload.id,
        changes: {
          name: action.payload.data.name,
          power: action.payload.data.power,
          lumenOutput: action.payload.data.lumenOutput,
          colorTemperature: action.payload.data.colorTemperature,
          manufacturerUrl: action.payload.data.manufacturerUrl,
          specifications: {
            absolutePhotometry: data.absolutePhotometry != null ? String(data.absolutePhotometry) : undefined,
            luminousFlux: action.payload.data.lumenOutput
              ? `${Math.round(action.payload.data.lumenOutput).toLocaleString()} lm`
              : undefined,
            power: action.payload.data.power ? `${action.payload.data.power}W` : undefined,
            colorTemperature: action.payload.data.colorTemperature
              ? `${action.payload.data.colorTemperature} K`
              : undefined,
            cri: data.cri != null ? String(data.cri) : undefined,
            lifespan: data.lifespan != null ? `${data.lifespan} h` : undefined,
            ipRating: data.ipRating || undefined,
          },
        },
      }),
    );
  } catch (err: unknown) {
    console.error('Failed to update variant:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to update variant';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

function* removeVariantHandler(action: PayloadAction<string>) {
  yield put(setLoading(true));
  yield put(setError(null));
  try {
    yield call(httpDELETE, `/catalogue/variants/${action.payload}`, {});
    yield put(removeVariantFromList(action.payload));
  } catch (err: unknown) {
    console.error('Failed to delete variant:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to delete variant';
    yield put(setError(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}

// ─── Sync ────────────────────────────────────────────────────────────────────

function* syncCatalogueHandler() {
  try {
    yield call(httpPOST, '/catalogue/sync', {});
    yield put(showToast({ message: 'catalogue.syncStarted', type: 'success' }));
  } catch (err: unknown) {
    console.error('Failed to sync catalogue:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to sync catalogue';
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

// ─── Auto-fetch watchers (on selection changes) ──────────────────────────────
// Each watcher checks the fetchedIds cache before triggering an API call.
// Data is only re-fetched when the cache entry is missing (first load or after
// a CRUD mutation invalidated it).

function* watchManufacturerSelection(action: PayloadAction<string | null>) {
  if (!action.payload) return;
  const cached = (yield select(isManufacturerSeriesFetched, action.payload)) as boolean;
  if (!cached) {
    yield put(fetchSeriesByManufacturer(action.payload));
  }
}

function* watchSeriesSelection(action: PayloadAction<string | null>) {
  if (!action.payload) return;
  const cached = (yield select(isSeriesProductsFetched, action.payload)) as boolean;
  if (!cached) {
    yield put(fetchProductsBySeries(action.payload));
  }
}

function* watchProductSelection(action: PayloadAction<string | null>) {
  if (!action.payload) return;
  const cached = (yield select(isProductDetailFetched, action.payload)) as boolean;
  if (!cached) {
    yield put(fetchProductDetail(action.payload));
  }
}

// ─── Root catalogue saga ─────────────────────────────────────────────────────

export default function* catalogueSaga() {
  yield all([
    // Fetch
    takeLatest(fetchManufacturers, fetchManufacturersHandler),
    takeLatest(fetchSeriesByManufacturer, fetchSeriesByManufacturerHandler),
    takeLatest(fetchProductsBySeries, fetchProductsBySeriesHandler),
    takeLatest(fetchProductDetail, fetchProductDetailHandler),
    // Manufacturer CRUD
    takeEvery(createManufacturer, createManufacturerHandler),
    takeEvery(modifyManufacturer, modifyManufacturerHandler),
    takeEvery(removeManufacturer, removeManufacturerHandler),
    // Series CRUD
    takeEvery(createSeries, createSeriesHandler),
    takeEvery(modifySeries, modifySeriesHandler),
    takeEvery(removeSeries, removeSeriesHandler),
    // Product CRUD
    takeEvery(createProduct, createProductHandler),
    takeEvery(modifyProduct, modifyProductHandler),
    takeEvery(removeProduct, removeProductHandler),
    // Variant CRUD
    takeEvery(createVariant, createVariantHandler),
    takeEvery(modifyVariant, modifyVariantHandler),
    takeEvery(removeVariant, removeVariantHandler),
    // Sync
    takeLatest(syncCatalogue, syncCatalogueHandler),
    // Auto-fetch on selection
    takeLatest(selectManufacturer, watchManufacturerSelection),
    takeLatest(selectSeries, watchSeriesSelection),
    takeLatest(selectProduct, watchProductSelection),
  ]);
}
